"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ObservablePromise = void 0;
const mobx_1 = require("mobx");
class ObservablePromise {
    constructor(action, parser, name) {
        this.name = name;
        this._instanceHooks = [];
        this.result = null;
        this.error = null;
        this.isExecuting = false;
        this.isError = false;
        this.wasExecuted = false;
        this._isWaitingForResponse = false;
        this._currentCall = null;
        this._queued = null;
        this.retry = () => this.reload();
        this._action = action;
        this._parser = parser;
    }
    get promise() {
        return this._promise;
    }
    get args() {
        return this._currentCall
            ? this._currentCall.args
            : [];
    }
    get isExecutingFirstTime() {
        return !this.wasExecuted && this.isExecuting;
    }
    /**
     * @deprecated use {@link wasSuccessful}
     */
    get wasExecutedSuccessfully() {
        return this.wasSuccessful;
    }
    get wasSuccessful() {
        return this.wasExecuted && !this.isError;
    }
    static registerHook(hook) {
        ObservablePromise._hooks.push(hook);
    }
    getResultOrDefault(...args) {
        if (args.length == 1) {
            if (!this.wasSuccessful)
                return args[0];
            return this.result;
        }
        else {
            if (!this.wasSuccessful)
                return args[1];
            return args[0](this.result);
        }
    }
    registerHook(hook) {
        this._instanceHooks.push(hook);
        return () => this.unregisterHook(hook);
    }
    registerHookOnce(hook) {
        const onceHook = (promise) => {
            this.unregisterHook(onceHook);
            hook(promise);
        };
        this.registerHook(onceHook);
        return () => this.unregisterHook(onceHook);
    }
    chain(promise) {
        return this.registerHook(() => {
            if (this.wasSuccessful)
                promise.resolve(this.result);
            else if (this.isError)
                promise.reject(this.error);
        });
    }
    chainResolve(promise) {
        return this.registerHook(() => {
            if (this.wasSuccessful)
                promise.resolve(this.result);
        });
    }
    chainReject(promise) {
        return this.registerHook(() => {
            if (this.isError)
                promise.reject(this.error);
        });
    }
    unregisterHook(hook) {
        this._instanceHooks = this._instanceHooks.filter(h => h != hook);
    }
    execute(...callArgs) {
        if (this._isWaitingForResponse) {
            if (this._queued)
                this._promise = this._promise.finally(() => this.execute(...callArgs));
            return this;
        }
        mobx_1.runInAction(() => {
            this.isExecuting = true;
        });
        this._isWaitingForResponse = true;
        this._currentCall = { args: callArgs, result: null };
        this._promise = new Promise((resolve, reject) => {
            this._action(...callArgs)
                .then((result) => {
                if (result instanceof Error)
                    this.handleError(result, reject);
                else {
                    if (this._parser) {
                        try {
                            result = this._parser(result, callArgs);
                        }
                        catch (e) {
                            result = e;
                        }
                        if (result instanceof Error) {
                            this.handleError(result, reject);
                            return result;
                        }
                    }
                    this.handleSuccess(result, resolve);
                }
                return result;
            })
                .catch((error) => {
                this.handleError(error, reject);
            });
        });
        return this;
    }
    reload() {
        return this.execute(...this._currentCall.args);
    }
    then(onResolved) {
        if (!this._promise)
            throw new Error('You have to call Request::execute before you can access it as promise');
        return this._promise.then(onResolved);
    }
    catch(onRejected = e => null) {
        if (!this._promise)
            throw new Error('You have to call Request::execute before you can access it as promise');
        return this._promise.catch(onRejected);
    }
    _triggerHooks() {
        this._instanceHooks.forEach(hook => hook(this));
        ObservablePromise._hooks.forEach(hook => hook(this));
    }
    resolve(result) {
        this.handleSuccess(result, null);
    }
    reject(error) {
        this.handleError(error, null);
    }
    queued(value = true) {
        this._queued = value;
        return this;
    }
    reset() {
        this.result = null;
        this.isExecuting = false;
        this.isError = false;
        this.wasExecuted = false;
        this._isWaitingForResponse = false;
        this._promise = null;
        return this;
    }
    ;
    clone() {
        return new ObservablePromise(this._action, this._parser, this.name);
    }
    handleSuccess(result, resolve) {
        this.result = result;
        if (this._currentCall)
            this._currentCall.result = result;
        this.isExecuting = false;
        this.isError = false;
        this.wasExecuted = true;
        this._isWaitingForResponse = false;
        this._triggerHooks();
        resolve && resolve(result);
    }
    handleError(error, reject) {
        this.error = error;
        this.isExecuting = false;
        this.isError = true;
        this.wasExecuted = true;
        this._isWaitingForResponse = false;
        this._triggerHooks();
        reject && reject(error);
    }
}
ObservablePromise._hooks = [];
__decorate([
    mobx_1.observable
], ObservablePromise.prototype, "result", void 0);
__decorate([
    mobx_1.observable
], ObservablePromise.prototype, "error", void 0);
__decorate([
    mobx_1.observable
], ObservablePromise.prototype, "isExecuting", void 0);
__decorate([
    mobx_1.observable
], ObservablePromise.prototype, "isError", void 0);
__decorate([
    mobx_1.observable
], ObservablePromise.prototype, "wasExecuted", void 0);
__decorate([
    mobx_1.computed
], ObservablePromise.prototype, "isExecutingFirstTime", null);
__decorate([
    mobx_1.computed
], ObservablePromise.prototype, "wasExecutedSuccessfully", null);
__decorate([
    mobx_1.computed
], ObservablePromise.prototype, "wasSuccessful", null);
__decorate([
    mobx_1.action
], ObservablePromise.prototype, "reset", null);
__decorate([
    mobx_1.action
], ObservablePromise.prototype, "handleSuccess", null);
__decorate([
    mobx_1.action
], ObservablePromise.prototype, "handleError", null);
exports.ObservablePromise = ObservablePromise;
